import { Channel, ChannelQualityVariant, ChannelResolution } from '../types/iptv';

export const RESOLUTION_HIERARCHY: Record<ChannelResolution, number> = {
  SD: 1,
  HD: 2,
  FHD: 3,
  '4K': 4,
};

/**
 * Verifica se a categoria do canal é puramente técnica de resolução (ex: 'CANAIS FHD', '4K ULTRA HD')
 */
export function isTechnicalCategory(groupTitle?: string): boolean {
  if (!groupTitle) return true;
  const g = groupTitle.trim().toLowerCase();
  return (
    /^(?:canais\s*)?(?:fhd|4k|uhd|hd|sd)(?:\s*(?:ultra\s*hd|canais|geral))?$/i.test(g) ||
    /^(?:4k\s*ultra\s*hd|ultra\s*4k|full\s*hd|canais\s*4k|canais\s*fhd|canais\s*hd|canais\s*sd)$/i.test(g)
  );
}

/**
 * Detects resolution tag in channel name (or group) and extracts clean base channel name.
 */
export function extractChannelQualityInfo(rawName: string, rawGroupTitle?: string): {
  baseName: string;
  detectedResolution: ChannelResolution | null;
  quality?: ChannelResolution | null;
} {
  let name = (rawName || '').trim();
  let detected: ChannelResolution | null = null;

  // 1. Detect 4K / UHD / H.265 / HEVC
  if (/\b(?:4k|uhd|2160p|hevc|h\.?265)\b/i.test(name) || /[\(\[\{]\s*(?:4k|uhd|2160p|hevc|h\.?265)\s*[\)\]\}]/i.test(name)) {
    detected = '4K';
  }
  // 2. Detect FHD / 1080p / Full HD
  else if (/\b(?:fhd|1080p|1080i|full\s*hd)\b/i.test(name) || /[\(\[\{]\s*(?:fhd|1080p|1080i|full\s*hd)\s*[\)\]\}]/i.test(name)) {
    detected = 'FHD';
  }
  // 3. Detect SD / 480p / 360p
  else if (/\b(?:sd|480p|360p)\b/i.test(name) || /[\(\[\{]\s*(?:sd|480p|360p)\s*[\)\]\}]/i.test(name)) {
    detected = 'SD';
  }
  // 4. Detect HD / 720p
  else if (/\b(?:hd|720p)\b/i.test(name) || /[\(\[\{]\s*(?:hd|720p)\s*[\)\]\}]/i.test(name)) {
    detected = 'HD';
  }

  // Se não detectou no nome, tenta inferir a partir da categoria técnica
  if (!detected && rawGroupTitle) {
    if (/\b(?:4k|uhd|2160p|hevc|h\.?265)\b/i.test(rawGroupTitle)) {
      detected = '4K';
    } else if (/\b(?:fhd|1080p|1080i|full\s*hd)\b/i.test(rawGroupTitle)) {
      detected = 'FHD';
    } else if (/\b(?:hd|720p)\b/i.test(rawGroupTitle)) {
      detected = 'HD';
    } else if (/\b(?:sd|480p|360p)\b/i.test(rawGroupTitle)) {
      detected = 'SD';
    }
  }

  // Remove country prefix: 'BR: ', 'BR | ', '[BR] ', 'BRASIL | ', 'PT-BR: ', etc.
  name = name.replace(/^[\[\(]?(?:br|brasil|bra|pt|pt-br|brazil|latam|us|usa)[\]\)]?\s*[-|/:]?\s*/gi, '');

  // Remove resolution prefix at beginning: '4K | ', 'FHD - ', '[HD] ', 'SD: ', 'H265 | ', etc.
  name = name.replace(/^[\[\(]?(?:4k|uhd|2160p|fhd|1080p|1080i|hd|720p|sd|480p|360p|hevc|h\.?265)[\]\)]?\s*[-|/:]?\s*/gi, '');

  // Clean the resolution tags from the name to get canonical base name
  let cleanName = name
    // 1. Remove bracketed resolutions, codecs, or backup/option tags
    .replace(/[\(\[\{]\s*(?:4k|uhd|2160p|fhd|1080p|1080i|hd|720p|sd|480p|360p|hevc|h\.?265|60\s*fps|opção\s*\d+|opcao\s*\d+|alt\s*\d*|backup\s*\d*|\d+)\s*[\)\]\}]/gi, ' ')
    // 2. Remove trailing alternative tags, superscript digits, or backup markers
    .replace(/\s*(?:[¹²³⁴⁵⁶⁷⁸⁹⁰]+|\b(?:alt|backup|opção\s*\d+|opcao\s*\d+|fonte\s*\d+|servidor\s*\d+)\b)\s*$/gi, ' ')
    // 3. Remove resolution followed by optional alternative number or superscript
    .replace(/\s*[-|/:]?\s*(?:4k|uhd|2160p|fhd|1080p|1080i|hd|720p|sd|480p|360p|hevc|h\.?265)\s*(?:[¹²³⁴⁵⁶⁷⁸⁹⁰]+|\b\d+\b)?\s*$/gi, ' ')
    // 4. Remove resolution preceded by dash, pipe, slash or colon in middle
    .replace(/\s*[-|/:]\s*(?:4k|uhd|2160p|fhd|1080p|1080i|hd|720p|sd|480p|360p|hevc|h\.?265)\b/gi, ' ')
    // 5. Remove standalone resolution words
    .replace(/\b(?:4k|uhd|2160p|fhd|1080p|1080i|hd|720p|sd|480p|360p|hevc|h\.?265)\b/gi, ' ')
    // 6. Remove extra codec/fps descriptors like H.265, HEVC, 60FPS
    .replace(/\b(?:hevc|h\.?265|60\s*fps)\b/gi, ' ')
    // 7. Remove leftover trailing or leading superscripts / separators
    .replace(/\s*[¹²³⁴⁵⁶⁷⁸⁹⁰]+\s*$/gi, ' ')
    .replace(/\s*[-|/:]+\s*$/gi, ' ')
    .replace(/^\s*[-|/:]+\s*/gi, ' ')
    // 8. Collapse whitespace
    .replace(/\s+/g, ' ')
    .trim();

  // If cleaning resulted in empty (edge case), keep original
  if (!cleanName) {
    cleanName = rawName.trim();
  }

  return {
    baseName: cleanName,
    detectedResolution: detected,
    quality: detected,
  };
}

/**
 * Groups multiple channel variations (SD, HD, FHD, 4K) into a single canonical channel.
 * Sets the default streamUrl to SD (or the lowest available resolution) for ultra-fast zapping.
 */
export function groupChannelsByQuality(
  channels: Channel[],
  excludedBaseKeys?: Set<string>,
  mergeAllEnabled: boolean = true,
  excludedQualitiesMap?: Record<string, ChannelResolution[]>,
  manualMergeGroups?: { id: string; baseName: string; channelIds: string[] }[],
  excludedChannelIds?: Set<string>,
  deletedMergeGroupKeys?: Set<string>
): Channel[] {
  if (!channels || channels.length === 0) return [];
  if (!mergeAllEnabled) return channels;

  const map = new Map<string, {
    canonicalChannel: Channel;
    variants: Map<ChannelResolution, ChannelQualityVariant>;
  }>();

  const unmergedChannels: Channel[] = [];

  // Mapeia IDs para canais de grupos manuais se existirem
  const manualGroupMap = new Map<string, string>(); // channelId -> manualGroupId
  if (manualMergeGroups && manualMergeGroups.length > 0) {
    for (const mg of manualMergeGroups) {
      for (const chId of mg.channelIds) {
        manualGroupMap.set(chId, mg.baseName);
      }
    }
  }

  for (const ch of channels) {
    const { baseName: naturalBaseName, detectedResolution } = extractChannelQualityInfo(ch.name, ch.groupTitle);
    const resolvedRes: ChannelResolution = detectedResolution || ch.resolution || 'SD';

    const manualTargetName = manualGroupMap.get(ch.id);
    const baseName = manualTargetName || naturalBaseName;

    // Canonical key baseada na identidade real do canal
    // Se for canal adulto (+18), isola com namespace para segurança
    const isAdult = /adult|xxx|18\+|erotico|playboy|sexy|venus/i.test((ch.groupTitle || '') + ' ' + ch.name);
    const cleanBaseKey = baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const groupKey = isAdult ? `adult:::${cleanBaseKey}` : cleanBaseKey;

    // 1. Se a mesclagem deste canal/grupo foi excluída pelo usuário
    if (deletedMergeGroupKeys && (deletedMergeGroupKeys.has(cleanBaseKey) || deletedMergeGroupKeys.has(groupKey))) {
      unmergedChannels.push(ch);
      continue;
    }

    // 2. Se este canal específico (ID) foi excluído da mesclagem pelo usuário (botão X na variante)
    if (excludedChannelIds && excludedChannelIds.has(ch.id)) {
      unmergedChannels.push(ch);
      continue;
    }

    // 3. Se o canal estiver na lista de canais desmembrados (separados temporariamente na grade)
    if (excludedBaseKeys && (excludedBaseKeys.has(cleanBaseKey) || excludedBaseKeys.has(groupKey))) {
      unmergedChannels.push(ch);
      continue;
    }

    // 4. Se esta qualidade específica foi excluída da mesclagem pelo usuário
    const excludedQualities = (excludedQualitiesMap && (excludedQualitiesMap[cleanBaseKey] || excludedQualitiesMap[groupKey])) || [];
    if (excludedQualities.includes(resolvedRes)) {
      unmergedChannels.push(ch);
      continue;
    }

    const variant: ChannelQualityVariant = {
      resolution: resolvedRes,
      streamUrl: ch.streamUrl,
      originalName: ch.name,
    };

    const existing = map.get(groupKey);
    if (!existing) {
      // Primeira vez vendo este canal canônico
      const variantsMap = new Map<ChannelResolution, ChannelQualityVariant>();
      variantsMap.set(resolvedRes, variant);

      // Preserva e mescla variantes se ch já possuía qualities previamente consolidadas
      if (ch.qualities && Array.isArray(ch.qualities)) {
        for (const q of ch.qualities) {
          if (q && q.resolution && q.streamUrl) {
            if (!variantsMap.has(q.resolution)) {
              variantsMap.set(q.resolution, q);
            }
          }
        }
      }

      map.set(groupKey, {
        canonicalChannel: {
          ...ch,
          name: baseName, // Nome limpo canônico (sem tags de SD/HD)
        },
        variants: variantsMap,
      });
    } else {
      // Canal já existente: mescla variante
      if (!existing.variants.has(resolvedRes)) {
        existing.variants.set(resolvedRes, variant);
      }

      // Preserva e mescla variantes se ch já possuía qualities previamente consolidadas
      if (ch.qualities && Array.isArray(ch.qualities)) {
        for (const q of ch.qualities) {
          if (q && q.resolution && q.streamUrl) {
            if (!existing.variants.has(q.resolution)) {
              existing.variants.set(q.resolution, q);
            }
          }
        }
      }

      // Prioriza categoria de conteúdo (ex: 'ESPORTES', 'ABERTOS') sobre pastas técnicas ('CANAIS FHD')
      if (isTechnicalCategory(existing.canonicalChannel.groupTitle) && !isTechnicalCategory(ch.groupTitle)) {
        existing.canonicalChannel.groupTitle = ch.groupTitle;
      }

      // Preserva número de canal válido se o atual não tiver
      if (!existing.canonicalChannel.num && ch.num) {
        existing.canonicalChannel.num = ch.num;
      }

      // Se o canal recebido tiver um logo melhor ou EPG, atualiza
      if ((!existing.canonicalChannel.logo || existing.canonicalChannel.logo.includes('placeholder')) && ch.logo) {
        existing.canonicalChannel.logo = ch.logo;
      }
      if (!existing.canonicalChannel.epgId && ch.epgId) {
        existing.canonicalChannel.epgId = ch.epgId;
      }

      // Mescla URLs de backup
      const backupSet = new Set(existing.canonicalChannel.backupStreams || []);
      if (ch.streamUrl && ch.streamUrl !== existing.canonicalChannel.streamUrl) {
        backupSet.add(ch.streamUrl);
      }
      if (ch.backupStreams && ch.backupStreams.length > 0) {
        for (const u of ch.backupStreams) {
          if (u) backupSet.add(u);
        }
      }
      existing.canonicalChannel.backupStreams = Array.from(backupSet);
    }
  }

  // Converte mapa agrupado para lista de canais consolidados
  const consolidatedChannels: Channel[] = [];

  for (const { canonicalChannel, variants } of map.values()) {
    // Ordena as qualidades disponíveis: SD -> HD -> FHD -> 4K
    const sortedVariants = Array.from(variants.values()).sort(
      (a, b) => RESOLUTION_HIERARCHY[a.resolution] - RESOLUTION_HIERARCHY[b.resolution]
    );

    // Preferência de início no SD para zapping ultra-rápido, ou menor resolução disponível
    const preferredInitial = variants.get('SD') || sortedVariants[0];

    canonicalChannel.qualities = sortedVariants;
    canonicalChannel.currentQuality = preferredInitial.resolution;
    canonicalChannel.streamUrl = preferredInitial.streamUrl;
    canonicalChannel.resolution = preferredInitial.resolution;

    consolidatedChannels.push(canonicalChannel);
  }

  return [...consolidatedChannels, ...unmergedChannels];
}

/**
 * Expande canais pré-consolidados em memória ou vindos do banco de dados,
 * desmembrando cada variante em `c.qualities` como um canal individual autêntico
 * com seu próprio nome original, streamUrl único e resolução correspondente.
 */
export function expandChannelsToRaw(channels: Channel[]): Channel[] {
  if (!channels || channels.length === 0) return [];

  const expanded: Channel[] = [];
  const seenUrls = new Set<string>();

  for (const ch of channels) {
    if (ch.qualities && Array.isArray(ch.qualities) && ch.qualities.length > 1) {
      for (const q of ch.qualities) {
        if (!q.streamUrl) continue;
        const normUrl = q.streamUrl.trim().toLowerCase();
        if (seenUrls.has(normUrl)) continue;
        seenUrls.add(normUrl);

        // Detecta resolução exata da variante a partir do nome ou fallback da qualidade
        const { detectedResolution } = extractChannelQualityInfo(q.originalName || ch.name, ch.groupTitle);
        const resolved = q.resolution || detectedResolution || 'HD';

        expanded.push({
          ...ch,
          id: `${ch.id}__var_${resolved.toLowerCase()}`,
          name: q.originalName || `${ch.name} ${resolved}`,
          streamUrl: q.streamUrl,
          resolution: resolved,
          qualities: undefined,
          currentQuality: undefined,
        });
      }
    } else {
      const normUrl = (ch.streamUrl || '').trim().toLowerCase();
      const { detectedResolution } = extractChannelQualityInfo(ch.name, ch.groupTitle);
      const resolved = detectedResolution || ch.resolution || 'HD';

      const enrichedChannel: Channel = {
        ...ch,
        resolution: resolved,
      };

      if (normUrl && !seenUrls.has(normUrl)) {
        seenUrls.add(normUrl);
        expanded.push(enrichedChannel);
      } else if (!normUrl) {
        expanded.push(enrichedChannel);
      }
    }
  }

  return expanded;
}
